import React, { useState } from "react";
import {
    useGetIncentiveFundSummaryQuery,
    useGetIncentiveFundPaymentsQuery,
    useGetIncentiveFundPayoutsQuery,
    useCreateIncentiveFundPayoutMutation,
    useGetProgramsQuery,
} from "../api";
import { IncentiveFundParams } from "../api/incentiveFund";
import { Plus, History, Wallet, TrendingUp, TrendingDown, Search, Filter } from "lucide-react";
import { UserViewModal } from "../components/UserViewModal";

type Tab = "payments" | "payouts";

const IncentiveFundPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>("payments");
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    
    // Filters state
    const [filters, setFilters] = useState<IncentiveFundParams>({
        page: 1,
        per_page: 20,
        user_id: "",
        program_id: "",
        date_from: "",
        date_to: "",
    });

    // Summary query
    const { data: summary, isLoading: summaryLoading } = useGetIncentiveFundSummaryQuery(filters);
    
    // Programs for filter
    const { data: programs } = useGetProgramsQuery();

    // Payments tab
    const { data: paymentsData, isLoading: paymentsLoading } = useGetIncentiveFundPaymentsQuery(filters, {
        skip: activeTab !== "payments"
    });

    // Payouts tab
    const { data: payoutsData, isLoading: payoutsLoading } = useGetIncentiveFundPayoutsQuery({
        page: filters.page,
        per_page: filters.per_page,
        date_from: filters.date_from,
        date_to: filters.date_to,
    }, {
        skip: activeTab !== "payouts"
    });

    // Create payout mutation
    const [createPayout, { isLoading: isCreatingPayout }] = useCreateIncentiveFundPayoutMutation();
    const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
    const [payoutForm, setPayoutForm] = useState({ amount: "", comment: "" });

    const handleCreatePayout = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!payoutForm.amount) return;
        
        try {
            await createPayout({
                amount: parseFloat(payoutForm.amount),
                comment: payoutForm.comment
            }).unwrap();
            setIsPayoutModalOpen(false);
            setPayoutForm({ amount: "", comment: "" });
            alert("Выплата успешно добавлена");
        } catch (err: any) {
            alert(err?.data?.message || "Ошибка при создании выплаты");
        }
    };

    const handleUserClick = (userId: number) => {
        setSelectedUserId(userId);
        setIsUserModalOpen(true);
    };

    const formatAmount = (v: number | string) =>
        Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const formatDate = (d: string) => new Date(d).toLocaleString("uk-UA");

    const renderPagination = (data: any, currentFilters: any, setFiltersFn: any) => {
        if (!data || data.last_page <= 1) return null;

        return (
            <div className="flex justify-between items-center px-4 py-3 border-t text-sm bg-gray-50/50">
                <button 
                    onClick={() => setFiltersFn((p: any) => ({ ...p, page: (p.page ?? 1) - 1 }))} 
                    disabled={(currentFilters.page ?? 1) <= 1} 
                    className="px-3 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-40 transition-colors"
                >
                    ← Назад
                </button>
                <span className="text-gray-600">
                    Страница <span className="font-semibold text-gray-900">{currentFilters.page}</span> из <span className="font-semibold text-gray-900">{data.last_page}</span>
                </span>
                <button 
                    onClick={() => setFiltersFn((p: any) => ({ ...p, page: (p.page ?? 1) + 1 }))} 
                    disabled={(currentFilters.page ?? 1) >= (data.last_page ?? 1)} 
                    className="px-3 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-40 transition-colors"
                >
                    Вперед →
                </button>
            </div>
        );
    };

    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">Поощрительный фонд</h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => setIsPayoutModalOpen(true)}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
                    >
                        <Plus className="w-4 h-4" />
                        Добавить выплату
                    </button>
                </div>
            </div>

            {/* ─── Global Balance Stats ────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
                        <TrendingUp className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Всего поступило</p>
                        <p className="text-2xl font-bold text-gray-900">${formatAmount(summary?.totals?.total_in ?? 0)}</p>
                    </div>
                </div>
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="p-3 bg-amber-50 rounded-xl text-amber-600">
                        <TrendingDown className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Всего выплачено</p>
                        <p className="text-2xl font-bold text-gray-900">${formatAmount(summary?.totals?.total_out ?? 0)}</p>
                    </div>
                </div>
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-4 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-10">
                        <Wallet className="w-16 h-16" />
                    </div>
                    <div className="p-3 bg-green-50 rounded-xl text-green-600">
                        <Wallet className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Текущий баланс</p>
                        <p className="text-2xl font-bold text-green-700">${formatAmount(summary?.totals?.balance ?? 0)}</p>
                    </div>
                </div>
            </div>

            {/* ─── Filters Row ─────────────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-[200px] space-y-1">
                        <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
                            <Search className="w-3 h-3" />
                            ID Пользователя
                        </label>
                        <input
                            type="number"
                            placeholder="Поиск по ID..."
                            value={filters.user_id}
                            onChange={(e) => setFilters((p) => ({ ...p, user_id: e.target.value, page: 1 }))}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        />
                    </div>
                    <div className="flex-1 min-w-[200px] space-y-1">
                        <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
                            <Filter className="w-3 h-3" />
                            Программа
                        </label>
                        <select
                            value={filters.program_id}
                            onChange={(e) => setFilters((p) => ({ ...p, program_id: e.target.value, page: 1 }))}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        >
                            <option value="">Все программы</option>
                            {programs?.filter(p => ['structural', 'star', 'charity'].includes(p.type)).map((prog) => (
                                <option key={prog.id} value={prog.id}>{prog.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-600">Дата с</label>
                        <input 
                            type="date" 
                            value={filters.date_from} 
                            onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value, page: 1 }))} 
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none" 
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-600">Дата по</label>
                        <input 
                            type="date" 
                            value={filters.date_to} 
                            onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value, page: 1 }))} 
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none" 
                        />
                    </div>
                    <button 
                        onClick={() => setFilters({ page: 1, per_page: 20, user_id: "", program_id: "", date_from: "", date_to: "" })}
                        className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200"
                    >
                        Сбросить
                    </button>
                </div>
            </div>

            {/* ─── Tabs ─────────────────────────────────────────────────────────── */}
            <div className="flex gap-1 bg-gray-200/50 p-1 rounded-xl w-fit">
                {(["payments", "payouts"] as Tab[]).map((tab) => {
                    const labels: Record<Tab, string> = { 
                        payments: "Платежи (In)", 
                        payouts: "Выплаты (Out)"
                    };
                    return (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                activeTab === tab ? "bg-white shadow-sm text-blue-600" : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                            }`}
                        >
                            {labels[tab]}
                        </button>
                    );
                })}
            </div>

            {/* ─── Filtered Results Summary ────────────────────────────────────── */}
            {summary?.filtered && (
                <div className="bg-blue-600 text-white rounded-xl p-4 flex justify-between items-center shadow-lg shadow-blue-600/20">
                    <div className="flex gap-8">
                        <div>
                            <p className="text-[10px] uppercase font-bold text-blue-200 tracking-wider">Сумма по фильтру (In)</p>
                            <p className="text-lg font-bold">${formatAmount(summary.filtered?.total_in ?? 0)}</p>
                        </div>
                        <div className="border-l border-blue-500 pl-8">
                            <p className="text-[10px] uppercase font-bold text-blue-200 tracking-wider">Выплаты по фильтру (Out)</p>
                            <p className="text-lg font-bold">-${formatAmount(summary.filtered?.total_out ?? 0)}</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase font-bold text-blue-200 tracking-wider text-right">Итого по фильтру</p>
                        <p className="text-xl font-bold text-right">${formatAmount(summary.filtered?.balance ?? 0)}</p>
                    </div>
                </div>
            )}

            {/* ─── Payments Tab Content ────────────────────────────────────────── */}
            {activeTab === "payments" && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {paymentsLoading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 uppercase text-[10px] font-bold tracking-widest">
                                    <tr>
                                        <th className="px-6 py-4">Пользователь</th>
                                        <th className="px-6 py-4">Программа</th>
                                        <th className="px-6 py-4 text-right">Сумма</th>
                                        <th className="px-6 py-4">Дата</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 text-gray-700">
                                    {paymentsData?.data?.map((p) => (
                                        <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <button 
                                                    onClick={() => handleUserClick(p.user_id)}
                                                    className="font-semibold text-blue-600 hover:text-blue-800 hover:underline flex flex-col items-start"
                                                >
                                                    {p.user ? `${p.user.name} ${p.user.surname}` : `Користувач #${p.user_id}`}
                                                    <span className="text-[10px] text-gray-400 font-mono">ID: {p.user_id}</span>
                                                </button>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                                                    {p.program?.name || `Програма #${p.program_id}`}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right font-bold text-gray-900">${formatAmount(p.amount)}</td>
                                            <td className="px-6 py-4 text-gray-500 tabular-nums">{formatDate(p.created_at)}</td>
                                        </tr>
                                    ))}
                                    {(!paymentsData?.data || paymentsData.data.length === 0) && (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-12 text-center text-gray-400 italic">Никаких платежей не найдено</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {renderPagination(paymentsData, filters, setFilters)}
                </div>
            )}

            {/* ─── Payouts Tab Content ─────────────────────────────────────────── */}
            {activeTab === "payouts" && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {payoutsLoading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 uppercase text-[10px] font-bold tracking-widest">
                                    <tr>
                                        <th className="px-6 py-4">ID</th>
                                        <th className="px-6 py-4 text-right">Сумма</th>
                                        <th className="px-6 py-4">Комментарий</th>
                                        <th className="px-6 py-4">Дата</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 text-gray-700">
                                    {payoutsData?.data?.map((p) => (
                                        <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-4 font-mono text-gray-400">#{p.id}</td>
                                            <td className="px-6 py-4 text-right font-bold text-red-600">-${formatAmount(p.amount)}</td>
                                            <td className="px-6 py-4 whitespace-pre-wrap">{p.comment || "-"}</td>
                                            <td className="px-6 py-4 text-gray-500 tabular-nums">{formatDate(p.created_at)}</td>
                                        </tr>
                                    ))}
                                    {(!payoutsData?.data || payoutsData.data.length === 0) && (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-12 text-center text-gray-400 italic">Никаких выплат не найдено</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {renderPagination(payoutsData, filters, setFilters)}
                </div>
            )}

            {/* ─── Payout Modal ─────────────────────────────────────────────────── */}
            {isPayoutModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
                        <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Новая выплата из фонда</h3>
                                <p className="text-xs text-gray-500">Заполните детали выплаты</p>
                            </div>
                            <button onClick={() => setIsPayoutModalOpen(false)} className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full transition-all">
                                <Plus className="w-6 h-6 rotate-45" />
                            </button>
                        </div>
                        <form onSubmit={handleCreatePayout} className="p-6 space-y-4">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Сумма выплаты ($) *</label>
                                <input 
                                    autoFocus
                                    required
                                    type="number" 
                                    step="0.01"
                                    min="0.01"
                                    value={payoutForm.amount}
                                    onChange={(e) => setPayoutForm(f => ({ ...f, amount: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-semibold text-lg"
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Комментарий</label>
                                <textarea 
                                    rows={3}
                                    value={payoutForm.comment}
                                    onChange={(e) => setPayoutForm(f => ({ ...f, comment: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-sm h-24"
                                    placeholder="Описание цели выплаты..."
                                />
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsPayoutModalOpen(false)}
                                    className="flex-1 px-4 py-3 text-sm font-bold text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                                >
                                    Отменить
                                </button>
                                <button
                                    type="submit"
                                    disabled={isCreatingPayout || !payoutForm.amount}
                                    className="flex-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-8 py-3 rounded-xl text-sm font-bold transition-all shadow-lg shadow-blue-600/20 active:scale-95"
                                >
                                    {isCreatingPayout ? "Создание..." : "Подтвердить выплату"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ─── User Modal ───────────────────────────────────────────────────── */}
            <UserViewModal 
                userId={selectedUserId}
                isOpen={isUserModalOpen}
                onClose={() => setIsUserModalOpen(false)}
            />
        </div>
    );
};

export default IncentiveFundPage;
